import { EventLogStorage, LocalStorageEventLogStorage } from './event-log-storage';
import { storage } from './utility/storage';
import { CURRENT_SESSION_STARTED_AT_STORAGE_KEY } from './constants';
import { TriggeredEvent } from './triggered-event.types';
import { EvaluationPeriodSecond, EventEvaluationType } from './messages/message-trigger.types';

class EventLogQueryError extends Error {}
class EventLogQuery {
  private eventLog: EventLog;
  private eventName: string;
  private evaluationType: EventEvaluationType | null;
  private evaluationPeriodSecond: EvaluationPeriodSecond | null;

  constructor(eventLog: EventLog, eventName: string) {
    this.eventLog = eventLog;
    this.eventName = eventName;
    this.evaluationType = null;
    this.evaluationPeriodSecond = null;
  }

  public setEvaluationType(evaluationType: EventEvaluationType): EventLogQuery {
    this.evaluationType = evaluationType;
    return this;
  }

  public setEvaluationPeriodSecond(evaluationPeriodSecond: EvaluationPeriodSecond): EventLogQuery {
    this.evaluationPeriodSecond = evaluationPeriodSecond;
    return this;
  }

  public execute(): Promise<TriggeredEvent[]> {
    return this.eventLog.fetch(this.eventName).then((events) => {
      if (this.evaluationType === null) {
        return events;
      }
      const evaluationTime = this.evaluateLimitTime();
      return events.filter((event) => event.tracked_at.getTime() >= evaluationTime);
    });
  }

  private evaluateLimitTime(): number {
    switch (this.evaluationType) {
      case 'session': {
        const sessionStartedAt = storage.get(CURRENT_SESSION_STARTED_AT_STORAGE_KEY);
        if (sessionStartedAt === null) {
          throw new EventLogQueryError('could not get session started at');
        }
        return parseInt(sessionStartedAt, 10);
      }
      case 'time_range': {
        if (this.evaluationPeriodSecond === null) {
          throw new EventLogQueryError('evaluationPeriodSecond is null');
        }
        return Date.now() - this.evaluationPeriodSecond * 1000;
      }
      default:
        throw new EventLogQueryError(`unknown evaluatetype:${this.evaluationType}`);
    }
  }
}

export class EventLog {
  private storage: EventLogStorage;
  private isWriteAllowed: boolean = true;

  constructor(args: { userIdentifier: string; isWriteAllowed: boolean }) {
    this.storage = this.buildStorage(args.userIdentifier);
    this.isWriteAllowed = args.isWriteAllowed;
  }

  public buildQuery(eventName: string): EventLogQuery {
    return new EventLogQuery(this, eventName);
  }

  public append(event: TriggeredEvent): Promise<void> {
    if (!this.isWriteAllowed) {
      return Promise.resolve();
    }
    return this.storage.append(event);
  }

  public fetch(eventName: string): Promise<TriggeredEvent[]> {
    return this.storage.fetch(eventName);
  }

  private buildStorage(userIdentifier: string): EventLogStorage {
    // TODO: 必要に応じて async なAPIで容量も豊富な indexedDB 版の EventLogStorage を実装すること
    return new LocalStorageEventLogStorage(userIdentifier);
  }
}
